import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BulkBudgetRecommendationLocation } from "@/features/campaigns/components/budgetRecommendation/BulkBudgetRecommendationProgress";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import {
  buildBudgetRecommendationState,
  getBulkBudgetRecommendationJobId,
  getBudgetRecommendationForBudget,
} from "@/features/campaigns/shared/helpers/budgetRecommendationHelper";
import { getDigitalCampaignLocationDisplayName } from "@/features/campaigns/digital/launch-digital-campaign/utils/locationDisplayName";

type LocationId = string | number;

type Campaign = Record<string, unknown> & {
  brand_contribution?: unknown;
  brand_contribution_type?: unknown;
  contribution_percentage?: unknown;
  contribution_max_limit?: unknown;
};

type Location = Record<string, unknown> & {
  id: LocationId;
  system_generated_location_id?: LocationId;
  location_name?: string | null;
  state?: string | null;
  country?: string | null;
  brand_region?: string | null;
  budget?: {
    brand_match_preset_amount?: string | number | null;
  };
};

type Recommendation = Record<string, unknown> & {
  locationId?: LocationId;
  location_id?: LocationId;
  system_generated_location_id?: LocationId;
  id?: LocationId;
  status?: string;
  finalRecommendation?: string | number | null;
  error?: string;
  message?: string;
};

type LocationFormData = Record<string, unknown> & {
  budget?: string | number | null;
  budgetRecommendation?: Recommendation | null;
  budgetRecommendationKey?: string | null;
  budgetRecommendationsByKey?: Record<string, Recommendation>;
  isBudgetRecommendationLoading?: boolean;
};

type RecommendationLocation = {
  loc: Location;
  locFormData: LocationFormData;
};

type BulkRecommendationProgress = {
  open: boolean;
  minimized: boolean;
  completed: number;
  total: number;
  locations: BulkBudgetRecommendationLocation[];
};

type BulkProgressPayload = {
  results?: Recommendation[];
  result?: Recommendation;
};

type UseBulkBudgetRecommendationOptions = {
  campaign: Campaign;
  values?: Record<string, LocationFormData>;
  subscribedLocations?: Location[];
  setFieldValue: (_locationId: LocationId, _value: LocationFormData) => unknown;
  onGetBulkBudgetRecommendation?: (_options: {
    locations: RecommendationLocation[];
    signal: AbortSignal;
  }) => Promise<unknown>;
  onStreamBulkBudgetRecommendationProgress?: (_options: {
    jobId: unknown;
    signal: AbortSignal;
    onMessage: (_payload?: BulkProgressPayload) => void;
  }) => Promise<unknown>;
};

const TERMINAL_STATUSES = new Set(["success", "error"]);

function isTerminalStatus(status: string) {
  return TERMINAL_STATUSES.has(status);
}

function getBulkResultLocationId(result: Recommendation = {}) {
  return (
    result.locationId ||
    result.location_id ||
    result.system_generated_location_id ||
    result.id
  );
}

function isBulkResultError(result: Recommendation = {}) {
  return ["error", "failed", "failure"].includes(
    String(result.status || "").toLowerCase()
  );
}

export default function useBulkBudgetRecommendation({
  campaign,
  values = {},
  subscribedLocations = [],
  setFieldValue,
  onGetBulkBudgetRecommendation,
  onStreamBulkBudgetRecommendationProgress,
}: UseBulkBudgetRecommendationOptions) {
  const abortControllersRef = useRef(new Set<AbortController>());
  const requestIdByLocationRef = useRef(new Map<string, symbol>());
  const recommendationLocationByRowIdRef = useRef(
    new Map<string, RecommendationLocation>()
  );
  const manuallyEditedLocationIdsRef = useRef(new Set<string>());
  const [progress, setProgress] = useState<BulkRecommendationProgress>({
    open: false,
    minimized: false,
    completed: 0,
    total: 0,
    locations: [],
  });

  useEffect(() => {
    const abortControllers = abortControllersRef.current;

    return () => {
      abortControllers.forEach((controller) => controller.abort());
      abortControllers.clear();
    };
  }, []);

  const getBudgetBrandContributionAmount = useCallback(
    ({
      loc,
      locFormData,
      budget,
    }: RecommendationLocation & { budget?: string | number | null }) => {
      const brandContributionAmount =
        joinCampaignHelper.getBrandContributionAmount({
          amount: Number(budget),
          brand_contribution: campaign?.brand_contribution,
          brand_contribution_type: campaign?.brand_contribution_type,
          contribution_percentage: campaign?.contribution_percentage,
          contribution_max_limit: campaign?.contribution_max_limit,
          brand_match_preset_amount:
            Number(loc?.budget?.brand_match_preset_amount) || 0,
        });
      const isInvalidBudget = joinCampaignHelper.getRequiredMinMax(
        { ...locFormData, budget },
        campaign
      );

      return budget && isInvalidBudget ? 0 : Number(brandContributionAmount);
    },
    [campaign]
  );

  const abortAllRequests = useCallback(() => {
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current.clear();
  }, []);

  const handleCancel = useCallback(() => {
    abortAllRequests();
    progress.locations.forEach((location) => {
      if (isTerminalStatus(location.status)) {
        return;
      }

      const recommendationLocation =
        recommendationLocationByRowIdRef.current.get(String(location.id));

      if (recommendationLocation) {
        setFieldValue(location.id, {
          ...recommendationLocation.locFormData,
          isBudgetRecommendationLoading: false,
        });
      }
    });
    setProgress((currentProgress) => ({
      ...currentProgress,
      open: false,
    }));
  }, [abortAllRequests, progress.locations, setFieldValue]);

  const handleToggleMinimized = useCallback(() => {
    setProgress((currentProgress) => ({
      ...currentProgress,
      minimized: !currentProgress.minimized,
    }));
  }, []);

  const handleBudgetChange = useCallback(
    (locationId: LocationId, enteredBudget?: number) => {
      const loc = subscribedLocations.find(
        (location) => String(location.id) === String(locationId)
      );
      const locFormData = values[String(locationId)];

      if (!loc || !locFormData) {
        return;
      }

      manuallyEditedLocationIdsRef.current.add(String(locationId));
      const progressLocation = progress.locations.find(
        (location) => String(location.id) === String(locationId)
      );
      if (progressLocation?.locationId) {
        manuallyEditedLocationIdsRef.current.add(
          String(progressLocation.locationId)
        );
      }

      setFieldValue(locationId, {
        ...locFormData,
        budget: enteredBudget,
        budgetRecommendation: getBudgetRecommendationForBudget(
          enteredBudget,
          locFormData
        ),
        isBudgetRecommendationLoading: false,
        brand_contribution_amount: getBudgetBrandContributionAmount({
          loc,
          locFormData,
          budget: enteredBudget,
        }),
      });
    },
    [
      getBudgetBrandContributionAmount,
      progress.locations,
      setFieldValue,
      subscribedLocations,
      values,
    ]
  );

  const handleResult = useCallback(
    ({
      result,
      recommendationLocationById,
      requestId,
    }: {
      result: Recommendation;
      recommendationLocationById: Map<string, RecommendationLocation>;
      requestId: symbol;
    }) => {
      const locationId = String(getBulkResultLocationId(result) || "");
      const recommendationLocation = recommendationLocationById.get(locationId);

      if (
        !recommendationLocation ||
        requestIdByLocationRef.current.get(locationId) !== requestId
      ) {
        return;
      }

      const { loc, locFormData } = recommendationLocation;
      if (
        manuallyEditedLocationIdsRef.current.has(String(loc.id)) ||
        manuallyEditedLocationIdsRef.current.has(locationId)
      ) {
        return;
      }

      const recommendedBudget = result.finalRecommendation;
      const nextStatus: BulkBudgetRecommendationLocation["status"] =
        isBulkResultError(result) ||
        recommendedBudget === undefined ||
        recommendedBudget === null
          ? "error"
          : "success";
      const error =
        result.error ||
        result.message ||
        "Budget recommendation is not available for this location.";

      if (nextStatus === "success") {
        const nextLocationData = {
          ...locFormData,
          budget: recommendedBudget,
          ...buildBudgetRecommendationState({
            locFormData,
            recommendation: result,
          }),
          isBudgetRecommendationLoading: false,
          brand_contribution_amount: getBudgetBrandContributionAmount({
            loc,
            locFormData,
            budget: recommendedBudget,
          }),
        };

        setFieldValue(loc.id, {
          ...nextLocationData,
          budgetRecommendation: getBudgetRecommendationForBudget(
            recommendedBudget,
            nextLocationData
          ),
        });
      } else {
        setFieldValue(loc.id, {
          ...locFormData,
          isBudgetRecommendationLoading: false,
        });
      }

      setProgress((currentProgress) => {
        const locations = currentProgress.locations.map((location) => {
          if (String(location.locationId) !== locationId) {
            return location;
          }

          return {
            ...location,
            status: nextStatus,
            recommendation: nextStatus === "success" ? result : null,
            error: nextStatus === "error" ? error : null,
          };
        });

        return {
          ...currentProgress,
          completed: locations.filter((location) =>
            isTerminalStatus(location.status)
          ).length,
          locations,
        };
      });
    },
    [getBudgetBrandContributionAmount, setFieldValue]
  );

  const runRequest = useCallback(
    async (recommendationLocations: RecommendationLocation[]) => {
      const requestId = Symbol("bulk-budget-recommendation-request");
      const recommendationLocationById = new Map<
        string,
        RecommendationLocation
      >();

      recommendationLocations.forEach(({ loc, locFormData }) => {
        const locationId = String(
          loc.system_generated_location_id || loc.id || ""
        );
        recommendationLocationById.set(locationId, { loc, locFormData });
        requestIdByLocationRef.current.set(locationId, requestId);
      });

      const abortController = new AbortController();
      abortControllersRef.current.add(abortController);

      try {
        const response = await onGetBulkBudgetRecommendation?.({
          locations: recommendationLocations,
          signal: abortController.signal,
        });
        const jobId = getBulkBudgetRecommendationJobId(response);

        if (!jobId) {
          throw new Error("Bulk recommendation job id was not returned.");
        }

        await onStreamBulkBudgetRecommendationProgress?.({
          jobId,
          signal: abortController.signal,
          onMessage: (payload = {}) => {
            const results = Array.isArray(payload.results)
              ? payload.results
              : payload.result
                ? [payload.result]
                : [];

            results.forEach((result) => {
              handleResult({
                result,
                recommendationLocationById,
                requestId,
              });
            });
          },
        });
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }

        const errorMessage =
          error instanceof Error && error.message
            ? error.message
            : "Budget recommendation could not be started for this location.";

        recommendationLocations.forEach(({ loc, locFormData }) => {
          const locationId = String(
            loc.system_generated_location_id || loc.id || ""
          );

          if (requestIdByLocationRef.current.get(locationId) !== requestId) {
            return;
          }

          setFieldValue(loc.id, {
            ...locFormData,
            isBudgetRecommendationLoading: false,
          });
        });

        setProgress((currentProgress) => {
          const locations = currentProgress.locations.map((location) => {
            const locationId = String(location.locationId || location.id);

            if (
              !recommendationLocationById.has(locationId) ||
              requestIdByLocationRef.current.get(locationId) !== requestId
            ) {
              return location;
            }

            return {
              ...location,
              status: "error" as const,
              recommendation: null,
              error: errorMessage,
            };
          });

          return {
            ...currentProgress,
            completed: locations.filter((location) =>
              isTerminalStatus(location.status)
            ).length,
            locations,
          };
        });
      } finally {
        abortControllersRef.current.delete(abortController);
      }
    },
    [
      handleResult,
      onGetBulkBudgetRecommendation,
      onStreamBulkBudgetRecommendationProgress,
      setFieldValue,
    ]
  );

  const handleRequested = useCallback(
    async (recommendationLocations: RecommendationLocation[] = []) => {
      if (!recommendationLocations.length) {
        return;
      }

      abortAllRequests();
      manuallyEditedLocationIdsRef.current = new Set();
      recommendationLocationByRowIdRef.current = new Map();

      recommendationLocations.forEach(({ loc, locFormData }) => {
        recommendationLocationByRowIdRef.current.set(String(loc.id), {
          loc,
          locFormData,
        });
        setFieldValue(loc.id, {
          ...locFormData,
          isBudgetRecommendationLoading: true,
          budgetRecommendation: getBudgetRecommendationForBudget(
            locFormData.budget,
            locFormData
          ),
        });
      });

      setProgress({
        open: true,
        minimized: false,
        completed: 0,
        total: recommendationLocations.length,
        locations: recommendationLocations.map(({ loc }) => ({
          id: loc.id,
          locationId: loc.system_generated_location_id || loc.id,
          name: getDigitalCampaignLocationDisplayName(loc) || "Location",
          status: "processing",
          marketData: loc,
        })),
      });

      await runRequest(recommendationLocations);
    },
    [abortAllRequests, runRequest, setFieldValue]
  );

  const handleRetry = useCallback(
    async (locationId: LocationId) => {
      const recommendationLocation =
        recommendationLocationByRowIdRef.current.get(String(locationId));

      if (!recommendationLocation) {
        return;
      }

      const retryLocation = {
        loc: recommendationLocation.loc,
        locFormData:
          values[String(locationId)] || recommendationLocation.locFormData,
      };
      recommendationLocationByRowIdRef.current.set(
        String(locationId),
        retryLocation
      );

      setFieldValue(locationId, {
        ...retryLocation.locFormData,
        isBudgetRecommendationLoading: true,
        budgetRecommendation: getBudgetRecommendationForBudget(
          retryLocation.locFormData.budget,
          retryLocation.locFormData
        ),
      });

      setProgress((currentProgress) => {
        const locations = currentProgress.locations.map((location) =>
          String(location.id) === String(locationId)
            ? {
                ...location,
                status: "processing" as const,
                recommendation: null,
                error: null,
              }
            : location
        );

        return {
          ...currentProgress,
          completed: locations.filter((location) =>
            isTerminalStatus(location.status)
          ).length,
          locations,
        };
      });

      await runRequest([retryLocation]);
    },
    [runRequest, setFieldValue, values]
  );

  const visibleLocations = useMemo(() => {
    return progress.locations.filter((location) => {
      if (location.status !== "success") {
        return true;
      }

      const locFormData = values[String(location.id)];
      return Boolean(
        getBudgetRecommendationForBudget(locFormData?.budget, locFormData)
      );
    });
  }, [progress.locations, values]);

  const visibleCompleted = useMemo(() => {
    return visibleLocations.filter((location) =>
      isTerminalStatus(location.status)
    ).length;
  }, [visibleLocations]);

  const removedLocationCount =
    progress.locations.length - visibleLocations.length;
  const isInProgress =
    progress.open &&
    progress.locations.some((location) => !isTerminalStatus(location.status));

  return {
    progress,
    visibleLocations,
    visibleCompleted,
    removedLocationCount,
    isInProgress,
    handleRequested,
    handleCancel,
    handleClose: handleCancel,
    handleBudgetChange,
    handleRetry,
    handleToggleMinimized,
  };
}
